import { buildAuditOutcomeFields, type TemplateOperationCompleteEvent } from 'adflow';

import { sanitizeLogValue } from '../webmcp/sanitize';
import { TRPCClient } from '../utils/apiClient';
import {
  forgetUndeliveredAudit,
  incrementUndeliveredAuditAttempts,
  listUndeliveredAuditInvocationIds,
  readUndeliveredAudit,
  rememberUndeliveredAudit,
  type UndeliveredAuditEntry,
  type UndeliveredAuditIngestPayload,
} from './auditOutbox';

const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;
const AUDIT_ATTEMPTS = 3;
const AUDIT_RETRY_DELAY_MS = 500;
const AUDIT_INGEST_TIMEOUT_MS = 10_000;

const TERMINAL_TRPC_CODES = new Set([
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'PRECONDITION_FAILED',
  'PAYLOAD_TOO_LARGE',
  'METHOD_NOT_SUPPORTED',
]);

function readTrpcClientErrorData(error: unknown): { code?: string; httpStatus?: number } | null {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return null;
  }
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== 'object') {
    return null;
  }
  const { code, httpStatus } = data as { code?: unknown; httpStatus?: unknown };
  return {
    code: typeof code === 'string' ? code : undefined,
    httpStatus: typeof httpStatus === 'number' ? httpStatus : undefined,
  };
}

function isTerminalIngestError(error: unknown): boolean {
  const trpcData = readTrpcClientErrorData(error);
  if (!trpcData) {
    return false;
  }
  if (trpcData.code && TERMINAL_TRPC_CODES.has(trpcData.code)) {
    return true;
  }
  const status = trpcData.httpStatus;
  if (status === undefined) {
    return false;
  }
  if (status >= 500) {
    return false;
  }
  if (status === 408 || status === 429) {
    return false;
  }
  return status >= 400 && status < 500;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function asSanitizedSummary(value: unknown): Record<string, unknown> | null {
  const sanitized = sanitizeLogValue(value);
  if (!sanitized || typeof sanitized !== 'object' || Array.isArray(sanitized)) {
    return null;
  }
  return sanitized as Record<string, unknown>;
}

function toOutboxEntry(
  event: TemplateOperationCompleteEvent,
  auditStatus: 'pending' | 'failed',
): UndeliveredAuditEntry {
  const fields = buildAuditOutcomeFields(event.result);
  return {
    invocationId: event.invocationId,
    source: event.source,
    toolName: event.toolName ?? null,
    operation: event.operation,
    outcome: fields.outcome,
    completedAt: new Date(event.completedAt).toISOString(),
    auditStatus,
    workingRevision: fields.workingRevision ?? null,
    errorCode: fields.errorCode ?? null,
  };
}

function buildIngestPayload(
  templateId: string,
  event: TemplateOperationCompleteEvent,
): UndeliveredAuditIngestPayload {
  const fields = buildAuditOutcomeFields(event.result);
  return {
    invocationId: event.invocationId,
    templateId,
    source: event.source,
    toolName: event.toolName ?? null,
    operation: event.operation,
    startedAt: new Date(event.startedAt).toISOString(),
    completedAt: new Date(event.completedAt).toISOString(),
    durationMs: event.durationMs,
    workingRevision: fields.workingRevision ?? null,
    persistedRevision: null,
    outcome: fields.outcome,
    errorCode: fields.errorCode ?? null,
    inputSummary: asSanitizedSummary(event.inputArgs),
    outputSummary: fields.outputSummary ?? null,
  };
}

type IngestMutateInput = {
  invocationId: string;
  templateId: string;
  source: 'ui' | 'webmcp';
  toolName: string | null;
  operation: string;
  startedAt: Date;
  completedAt: Date;
  durationMs: number;
  workingRevision: number | null;
  persistedRevision: number | null;
  outcome: string;
  errorCode: string | null;
  inputSummary: Record<string, unknown> | null;
  outputSummary: Record<string, unknown> | null;
};

type IngestOperationAuditClient = {
  ingestOperationAudit: {
    mutate: (
      input: IngestMutateInput,
      options?: { signal?: AbortSignal },
    ) => Promise<{ status: 'acknowledged' | 'pending'; invocationId: string }>;
  };
};

function toMutateInput(stored: UndeliveredAuditIngestPayload): IngestMutateInput {
  return {
    ...stored,
    startedAt: new Date(stored.startedAt),
    completedAt: new Date(stored.completedAt),
  };
}

async function deliverIngestPayload(stored: UndeliveredAuditIngestPayload): Promise<boolean> {
  const mutateInput = toMutateInput(stored);
  const client = TRPCClient.client.adTemplate as unknown as IngestOperationAuditClient;
  let lastError: unknown;
  for (let attempt = 0; attempt < AUDIT_ATTEMPTS; attempt += 1) {
    try {
      await client.ingestOperationAudit.mutate(mutateInput, {
        signal: AbortSignal.timeout(AUDIT_INGEST_TIMEOUT_MS),
      });
      forgetUndeliveredAudit(stored.invocationId);
      return true;
    } catch (error) {
      lastError = error;
      if (isTerminalIngestError(error)) {
        console.warn('[Template operation audit] ingest rejected (terminal)', error);
        forgetUndeliveredAudit(stored.invocationId);
        return false;
      }
      if (attempt < AUDIT_ATTEMPTS - 1) {
        await delay(AUDIT_RETRY_DELAY_MS);
      }
    }
  }
  console.warn('[Template operation audit] ingest failed', lastError);
  incrementUndeliveredAuditAttempts(stored.invocationId);
  return false;
}

function recordFailedDelivery(templateId: string, payload: UndeliveredAuditIngestPayload): void {
  if (!readUndeliveredAudit(payload.invocationId)) {
    return;
  }
  rememberUndeliveredAudit(templateId, rememberFailedEntryFromPayload(payload), payload);
}

export async function flushUndeliveredAuditsForTemplate(templateId: string): Promise<void> {
  if (!OBJECT_ID_PATTERN.test(templateId)) {
    return;
  }
  for (const invocationId of listUndeliveredAuditInvocationIds(templateId)) {
    const row = readUndeliveredAudit(invocationId);
    if (!row) {
      continue;
    }
    const delivered = await deliverIngestPayload(row.ingestPayload);
    if (!delivered) {
      recordFailedDelivery(templateId, row.ingestPayload);
    }
  }
}

function rememberFailedEntryFromPayload(
  payload: UndeliveredAuditIngestPayload,
): UndeliveredAuditEntry {
  return {
    invocationId: payload.invocationId,
    source: payload.source,
    toolName: payload.toolName,
    operation: payload.operation,
    outcome: payload.outcome,
    completedAt: payload.completedAt,
    auditStatus: 'failed',
    workingRevision: payload.workingRevision,
    errorCode: payload.errorCode,
  };
}

export async function retryUndeliveredAudit(invocationId: string): Promise<void> {
  const row = readUndeliveredAudit(invocationId);
  if (!row) {
    return;
  }
  const delivered = await deliverIngestPayload(row.ingestPayload);
  if (!delivered) {
    recordFailedDelivery(row.templateId, row.ingestPayload);
  }
}

export async function postTemplateOperationAudit(
  templateId: string,
  event: TemplateOperationCompleteEvent,
): Promise<void> {
  if (!OBJECT_ID_PATTERN.test(templateId)) {
    return;
  }

  const ingestPayload = buildIngestPayload(templateId, event);
  rememberUndeliveredAudit(templateId, toOutboxEntry(event, 'pending'), ingestPayload);

  const delivered = await deliverIngestPayload(ingestPayload);
  if (!delivered) {
    recordFailedDelivery(templateId, ingestPayload);
  }
}
